import { Link, useLocation } from "react-router-dom";
import { X, GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompare } from "@/contexts/CompareContext";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Floating bar shown when the user has selected at least one practice
 * to compare via the "Compare" checkbox on vet cards.
 */
const HIDE_ON_PREFIXES = ["/compare-vets", "/login", "/signup", "/vet-signup", "/consultation"];

const CompareFloatingBar = () => {
  const { items, remove, clear } = useCompare();
  const location = useLocation();
  const isMobile = useIsMobile();
  if (items.length === 0) return null;
  if (HIDE_ON_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const compareUrl = items.length >= 2
    ? `/compare-vets?ids=${items.map((it) => it.id).join(",")}`
    : "/compare-vets";

  return (
    <div
      role="region"
      aria-label="Compare practices"
      className="fixed z-40 left-1/2 -translate-x-1/2 bg-background/95 backdrop-blur-md border border-border shadow-xl rounded-full px-3 py-2 flex items-center gap-2 max-w-[calc(100vw-1rem)]"
      style={{ bottom: isMobile ? "calc(env(safe-area-inset-bottom) + 64px)" : "1rem" }}
    >
      <GitCompare className="w-4 h-4 text-primary shrink-0" aria-hidden />
      <span className="text-xs font-medium text-foreground hidden sm:inline whitespace-nowrap">
        Comparing {items.length}/3
      </span>
      <div className="flex items-center gap-1 overflow-x-auto max-w-[55vw]">
        {items.map((it) => (
          <span
            key={it.id}
            className="inline-flex items-center gap-1 bg-muted text-foreground text-xs rounded-full pl-2 pr-1 py-0.5 whitespace-nowrap"
          >
            <span className="truncate max-w-[8rem]">{it.name}</span>
            <button
              type="button"
              onClick={() => remove(it.id)}
              className="rounded-full hover:bg-background p-0.5"
              aria-label={`Remove ${it.name}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <Button asChild size="sm" disabled={items.length < 2} className="rounded-full h-8">
        <Link to={compareUrl} aria-disabled={items.length < 2}>Compare</Link>
      </Button>
      <button
        type="button"
        onClick={clear}
        className="text-xs text-muted-foreground hover:text-foreground px-1"
        aria-label="Clear comparison"
      >
        Clear
      </button>
    </div>
  );
};

export default CompareFloatingBar;
